import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Köttbutiken: bygg din egen köttlåda (sections/kb-bygg-lada.liquid, docs/bygg-din-lada.md).
 *
 * Kunden väljer storlek och fyller platserna i väljaren (en <dialog> med alla detaljer). Varje plats är en
 * variant. När lådan är full läggs lådprodukten och detaljerna i varukorgen i ett anrop: detaljerna som
 * nästlade rader (`parent_id`) under lådan, alla med den dolda egenskapen `_kb_lada` (lådans id). Valen sparas
 * i localStorage så att de finns kvar om sidan laddas om.
 *
 * @typedef {{ titel: string, variant: string, pris: number, fore: number, max: number, bild: string }} Variant
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [storleksnamn] - Vald storleks namn i rubriken.
 * @property {HTMLElement} raknare - "2/6" ovanför platserna.
 * @property {HTMLOListElement} platser - Lådans platser.
 * @property {HTMLTemplateElement} [stangIkon] - Kryssikonen till platsernas ta bort-knapp.
 * @property {HTMLElement} summa
 * @property {HTMLElement} summaNu
 * @property {HTMLElement} summaFore
 * @property {HTMLElement} summaSpara
 * @property {HTMLButtonElement} kop - Köpknappen.
 * @property {HTMLElement} fel - Felmeddelande.
 * @property {HTMLDialogElement} valjare - Väljaren med alla detaljer.
 * @property {HTMLElement} valjareStatus
 * @property {HTMLElement} valjareSumma
 *
 * @extends {Component<Refs>}
 */
class KbByggLadaComponent extends Component {
  /** Valda varianter i platsordning. @type {number[]} */
  #valda = [];
  /** @type {Map<number, Variant>} */
  #varianter = new Map();
  /** Varje korts varianter, för att markera kortet när någon av dem ligger i lådan. @type {WeakMap<HTMLElement, number[]>} */
  #kortVarianter = new WeakMap();
  #storlek = 0;
  #antal = 0;
  #rabatt = 0;
  /** Gåvorna på köpet för vald storlek (metafältet kottbutiken.kottlada). @type {{ id: number, antal: number, pris: number }[]} */
  #gavor = [];
  #gavorNamn = '';
  /** Platsen som byts ut när väljaren öppnades från en fylld plats. @type {number | null} */
  #byt = null;

  connectedCallback() {
    super.connectedCallback();

    for (const kort of this.#kort) {
      const element = kort.querySelector('[data-varianter]');
      try {
        const lista = JSON.parse(element?.textContent || '{}');
        const ids = Object.keys(lista).map(Number);
        for (const id of ids) this.#varianter.set(id, lista[id]);
        this.#kortVarianter.set(kort, ids);
      } catch {
        // Ett trasigt kort ska inte stoppa resten av väljaren
      }
    }

    const minne = this.#lasMinne();
    const radio = minne ? this.#storleksRadio(minne.storlek) : null;
    if (radio) radio.checked = true;
    this.#lasStorlek();
    if (minne) this.#valda = minne.valda.filter((id) => this.#varianter.has(id)).slice(0, this.#antal);
    this.#rensaOverLager();

    this.refs.valjare?.addEventListener('close', this.#efterStang);
    this.#rita();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.valjare?.removeEventListener('close', this.#efterStang);
  }

  /** Kunden byter storlek. Får valen inte plats tas de sista bort. */
  valjStorlek() {
    this.#lasStorlek();
    if (this.#valda.length > this.#antal) this.#valda = this.#valda.slice(0, this.#antal);
    this.#rita();
    this.#sparaMinne();
  }

  /**
   * Öppnar väljaren. Från en fylld plats byts den platsens detalj ut mot nästa val.
   * @param {Event} event
   */
  oppnaValjare(event) {
    const plats = Number(/** @type {HTMLElement} */ (event.target).dataset.plats);
    this.#byt = Number.isInteger(plats) && plats < this.#valda.length ? plats : null;
    this.#ritaValjare();
    this.refs.valjare.showModal();
  }

  stangValjare() {
    if (this.refs.valjare.open) this.refs.valjare.close();
  }

  /** Platserna ritas om medan väljaren är öppen, så fokus flyttas till nästa tomma plats eller köpknappen. */
  #efterStang = () => {
    this.#byt = null;
    const tom = /** @type {HTMLElement | null} */ (this.refs.platser.querySelector('.kb-bl__plats--tom'));
    (tom ?? this.refs.kop)?.focus();
  };

  /** @param {Event} event */
  filtrera(event) {
    const knapp = /** @type {HTMLElement} */ (event.target);
    const kategori = knapp.dataset.kategori ?? '';

    for (const flik of this.querySelectorAll('.kb-bl__flik')) {
      flik.setAttribute('aria-pressed', String(flik === knapp));
    }
    for (const kort of this.#kort) {
      const kategorier = (kort.dataset.kategorier ?? '').split(',');
      kort.hidden = kategori !== '' && !kategorier.includes(kategori);
    }
    this.querySelector('.kb-bl__produkter-wrapper')?.scrollTo({ top: 0 });
  }

  /** @param {Event} event */
  valjVariant(event) {
    const knapp = /** @type {HTMLElement} */ (event.target);
    const kort = /** @type {HTMLElement | null} */ (knapp.closest('.kb-bl-kort'));
    if (!kort || !knapp.dataset.variant) return;

    kort.dataset.vald = knapp.dataset.variant;
    for (const variant of kort.querySelectorAll('.kb-bl-kort__variant')) {
      variant.setAttribute('aria-pressed', String(variant === knapp));
    }
    this.#ritaKort(kort);
  }

  /**
   * Lägger kortets valda variant i lådan (eller byter ut platsen väljaren öppnades från).
   * Stänger väljaren när lådan blir full.
   * @param {Event} event
   */
  laggTill(event) {
    const knapp = /** @type {HTMLElement} */ (event.target);
    if (knapp.getAttribute('aria-disabled') === 'true') return;

    const id = this.#valdVariant(knapp);
    if (!id) return;

    if (this.#byt !== null) {
      const nuvarande = this.#valda[this.#byt];
      if (id !== nuvarande && this.#antalAv(id) >= this.#max(id)) return;
      this.#valda[this.#byt] = id;
      this.#byt = null;
      this.#rita();
      this.#sparaMinne();
      this.stangValjare();
      return;
    }

    if (this.#valda.length >= this.#antal || this.#antalAv(id) >= this.#max(id)) return;

    this.#valda.push(id);
    this.#rita();
    this.#sparaMinne();
    if (this.#valda.length >= this.#antal) setTimeout(() => this.stangValjare(), 350);
  }

  /** @param {Event} event */
  minska(event) {
    const id = this.#valdVariant(/** @type {HTMLElement} */ (event.target));
    const index = id ? this.#valda.lastIndexOf(id) : -1;
    if (index === -1) return;

    this.#valda.splice(index, 1);
    this.#rita();
    this.#sparaMinne();
  }

  /** @param {Event} event */
  taBort(event) {
    const plats = Number(/** @type {HTMLElement} */ (event.target).dataset.plats);
    if (!Number.isInteger(plats) || plats >= this.#valda.length) return;

    this.#valda.splice(plats, 1);
    this.#rita();
    this.#sparaMinne();

    const platser = this.refs.platser.querySelectorAll('.kb-bl__plats');
    /** @type {HTMLElement | undefined} */ (platser[Math.min(plats, this.#valda.length)])?.focus();
  }

  /** Lägger lådan i varukorgen. Är den inte full skakar platserna och väljaren öppnas. */
  async laggIVarukorgen() {
    const knapp = this.refs.kop;
    if (knapp.getAttribute('aria-busy') === 'true') return;

    if (this.#valda.length < this.#antal) {
      this.#skaka();
      this.#byt = null;
      this.#ritaValjare();
      this.refs.valjare.showModal();
      return;
    }

    const radio = this.#storleksRadio(this.#storlek);
    // Storlekens egen variant av lådprodukten, annars lådproduktens första
    const ladaVariant = Number(radio?.dataset.variant) || Number(this.dataset.ladaVariant);
    if (!ladaVariant) return;

    knapp.setAttribute('aria-busy', 'true');
    this.refs.fel.classList.add('hidden');

    const ladaId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

    /** @type {Map<number, number>} */
    const antalPer = new Map();
    for (const id of this.#valda) antalPer.set(id, (antalPer.get(id) ?? 0) + 1);

    const items = [
      {
        id: ladaVariant,
        quantity: 1,
        properties: {
          [this.#text('Egenskap')]: this.#text('StorlekEgenskap', {
            namn: radio?.dataset.namn ?? '',
            antal: this.#antal,
          }),
          _kb_lada: ladaId,
        },
      },
      ...Array.from(antalPer, ([id, quantity]) => ({
        id,
        quantity,
        parent_id: ladaVariant,
        properties: { _kb_lada: ladaId },
      })),
      // Gåvorna läggs under lådan med _kb_lada_gava; rabattfunktionen gör dem gratis när lådan är full
      ...this.#gavor.map((gava) => ({
        id: gava.id,
        quantity: gava.antal,
        parent_id: ladaVariant,
        properties: { _kb_lada: ladaId, _kb_lada_gava: '1' },
      })),
    ];

    /** @type {HTMLElement[]} */
    const cartItems = Array.from(document.querySelectorAll('cart-items-component'));
    const sections = cartItems.map((element) => element.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: Array.from(antalPer, ([id, quantity]) => ({ merchandiseId: String(id), quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
        })
      );
      const svar = await response.json();
      if (svar.status) throw new Error(svar.description || svar.message);

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: this.#valda.length,
          source: 'kb-bygg-lada',
          sections: svar.sections,
          didError: false,
        },
      });

      this.#valda = [];
      this.#rita();
      this.#sparaMinne();
    } catch (error) {
      const meddelande = (error instanceof Error && error.message) || this.#text('Fel');
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: meddelande, code: 'INVALID' }));
      this.refs.fel.textContent = meddelande;
      this.refs.fel.classList.remove('hidden');
    } finally {
      knapp.removeAttribute('aria-busy');
    }
  }

  /** Ritar platserna, summan och köpknappen (och väljaren om den är öppen). */
  #rita() {
    const { platser } = this.refs;
    const antal = this.#antal;
    platser.style.setProperty('--kb-bl-kolumner-mobil', String(antal <= 4 ? antal : antal <= 6 ? 3 : 4));
    platser.style.setProperty('--kb-bl-kolumner-dator', String(antal <= 6 ? antal : Math.ceil(antal / 2)));

    const ikon = this.refs.stangIkon?.innerHTML ?? '×';
    platser.innerHTML = Array.from({ length: antal }, (_, plats) => {
      const variant = this.#varianter.get(this.#valda[plats] ?? 0);
      if (!variant) {
        return `<li class="kb-bl__plats-wrapper"><button type="button" class="kb-bl__plats kb-bl__plats--tom" data-plats="${plats}" aria-label="${attr(this.#text('TomPlats', { nummer: plats + 1 }))}" on:click="/oppnaValjare"><span aria-hidden="true">+</span></button></li>`;
      }

      const namn = variant.variant ? `${variant.titel}, ${variant.variant}` : variant.titel;
      const bild = variant.bild ? `<img src="${attr(variant.bild)}" alt="" width="120" height="120" loading="lazy">` : '';
      const variantText = variant.variant ? `<span class="kb-bl__plats-variant">${html(variant.variant)}</span>` : '';
      return `<li class="kb-bl__plats-wrapper"><button type="button" class="kb-bl__plats" data-plats="${plats}" aria-label="${attr(this.#text('BytPlats', { titel: namn }))}" on:click="/oppnaValjare"><span class="kb-bl__plats-bild">${bild}</span><span class="kb-bl__plats-text"><span class="kb-bl__plats-titel">${html(variant.titel)}</span>${variantText}</span></button><button type="button" class="kb-bl__ta-bort" data-plats="${plats}" aria-label="${attr(this.#text('TaBort', { titel: namn }))}" on:click="/taBort">${ikon}</button></li>`;
    }).join('');

    const { total, spara, full } = this.#summa();
    this.refs.raknare.textContent = `${this.#valda.length}/${antal}`;

    this.refs.summa.hidden = this.#valda.length === 0;
    this.refs.summaNu.textContent = this.#pengar(total - spara);
    this.refs.summaFore.textContent = spara > 0 ? this.#pengar(total) : '';
    this.refs.summaFore.hidden = spara === 0;
    const gavovarde = this.#gavor.reduce((summa, gava) => summa + gava.pris * gava.antal, 0);
    this.refs.summaSpara.textContent = [
      spara > 0
        ? this.#text('DuSparar', { belopp: this.#pengar(spara), procent: this.#rabatt })
        : this.#rabatt > 0
          ? this.#text('SparaNarFull', { procent: this.#rabatt })
          : '',
      this.#gavorNamn
        ? full
          ? this.#text('GavorIngar', { gavor: this.#gavorNamn, belopp: this.#pengar(gavovarde) })
          : this.#text('FyllForGavor', { gavor: this.#gavorNamn.toLowerCase() })
        : '',
    ]
      .filter(Boolean)
      .join(' · ');

    this.refs.kop.textContent = full
      ? this.#text('LaggIVarukorgen', { belopp: this.#pengar(total - spara) })
      : this.#text('ValjFler', { antal: antal - this.#valda.length });
    this.refs.kop.setAttribute('aria-disabled', String(!full));

    this.#ritaValjare();
  }

  /** Väljarens status, summa och kort. */
  #ritaValjare() {
    const { total, spara } = this.#summa();
    const bytesUt = this.#byt === null ? undefined : this.#varianter.get(this.#valda[this.#byt] ?? 0);

    this.refs.valjareStatus.textContent = bytesUt
      ? this.#text('Byter', { titel: bytesUt.variant ? `${bytesUt.titel}, ${bytesUt.variant}` : bytesUt.titel })
      : this.#text('Raknare', { valda: this.#valda.length, antal: this.#antal });
    this.refs.valjareSumma.textContent = this.#valda.length ? this.#pengar(total - spara) : '';

    for (const kort of this.#kort) this.#ritaKort(kort);
  }

  /** @param {HTMLElement} kort */
  #ritaKort(kort) {
    const id = Number(kort.dataset.vald);
    const variant = this.#varianter.get(id);
    if (!variant) return;

    const antal = this.#antalAv(id);
    const bytLage = this.#byt !== null;
    const full = !bytLage && this.#valda.length >= this.#antal;
    const slut = antal >= variant.max && !(bytLage && this.#valda[this.#byt ?? -1] === id);

    const pris = kort.querySelector('[data-pris]');
    const fore = /** @type {HTMLElement | null} */ (kort.querySelector('[data-fore]'));
    if (pris) pris.textContent = this.#pengar(variant.pris);
    if (fore) {
      fore.textContent = variant.fore > variant.pris ? this.#pengar(variant.fore) : '';
      fore.hidden = !(variant.fore > variant.pris);
    }

    const lagg = /** @type {HTMLElement | null} */ (kort.querySelector('[data-lagg]'));
    const steg = /** @type {HTMLElement | null} */ (kort.querySelector('[data-steg]'));
    const visaSteg = antal > 0 && !bytLage;

    if (lagg) {
      lagg.hidden = visaSteg;
      lagg.textContent = full ? this.#text('Full') : bytLage ? this.#text('Byt') : this.#text('LaggTill');
      lagg.setAttribute('aria-disabled', String(full || slut));
    }
    if (steg) {
      steg.hidden = !visaSteg;
      const siffra = steg.querySelector('[data-antal]');
      if (siffra) siffra.textContent = String(antal);
      steg.querySelector('[data-oka]')?.setAttribute('aria-disabled', String(full || slut));
    }

    const ids = this.#kortVarianter.get(kort) ?? [];
    kort.classList.toggle('is-vald', ids.some((varianten) => this.#valda.includes(varianten)));
  }

  /** @returns {{ total: number, spara: number, full: boolean }} */
  #summa() {
    const total = this.#valda.reduce((summa, id) => summa + (this.#varianter.get(id)?.pris ?? 0), 0);
    const full = this.#valda.length >= this.#antal;
    const spara = full ? Math.round((total * this.#rabatt) / 100) : 0;
    return { total, spara, full };
  }

  #lasStorlek() {
    const radio = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
    this.#storlek = Number(radio?.value ?? 0);
    this.#antal = Number(radio?.dataset.antal) || this.refs.platser.children.length || 6;
    this.#rabatt = Number(radio?.dataset.rabatt) || 0;
    try {
      this.#gavor = JSON.parse(radio?.dataset.gavor || '[]');
    } catch {
      this.#gavor = [];
    }
    this.#gavorNamn = radio?.dataset.gavorNamn ?? '';
    if (this.refs.storleksnamn) this.refs.storleksnamn.textContent = radio?.dataset.namn ?? '';
  }

  /**
   * @param {number} index
   * @returns {HTMLInputElement | null}
   */
  #storleksRadio(index) {
    return this.querySelector(`input[type="radio"][value="${Number(index)}"]`);
  }

  /** Tar bort val som inte längre finns i lager (från minnet). */
  #rensaOverLager() {
    /** @type {Map<number, number>} */
    const raknade = new Map();
    this.#valda = this.#valda.filter((id) => {
      const antal = (raknade.get(id) ?? 0) + 1;
      raknade.set(id, antal);
      return antal <= this.#max(id);
    });
  }

  /**
   * Kortets valda variant, för knappar i ett produktkort.
   * @param {HTMLElement} element
   */
  #valdVariant(element) {
    const kort = /** @type {HTMLElement | null} */ (element.closest('.kb-bl-kort'));
    const id = Number(kort?.dataset.vald);
    return this.#varianter.has(id) ? id : 0;
  }

  /** @param {number} id */
  #antalAv(id) {
    return this.#valda.filter((vald) => vald === id).length;
  }

  /** @param {number} id */
  #max(id) {
    return this.#varianter.get(id)?.max ?? 0;
  }

  #skaka() {
    const { platser } = this.refs;
    platser.classList.remove('is-skakar');
    void platser.offsetWidth;
    platser.classList.add('is-skakar');
  }

  get #kort() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.kb-bl-kort')));
  }

  /**
   * Text från sektionens data-text-*-attribut, med [platshållare] utbytta.
   * @param {string} namn - T.ex. 'ValjFler' för data-text-valj-fler.
   * @param {Record<string, string | number>} [varden]
   */
  #text(namn, varden = {}) {
    let text = this.dataset[`text${namn}`] ?? '';
    for (const [nyckel, varde] of Object.entries(varden)) text = text.replaceAll(`[${nyckel}]`, String(varde));
    return text;
  }

  /** @param {number} belopp - I minsta enhet (öre). */
  #pengar(belopp) {
    return formatMoney(belopp, this.dataset.pengar || '{{amount}}', this.dataset.valuta || 'SEK');
  }

  get #minnesnyckel() {
    return `kb-bygg-lada:${this.dataset.ladaProdukt}`;
  }

  /** @returns {{ storlek: number, valda: number[] } | null} */
  #lasMinne() {
    try {
      const data = JSON.parse(localStorage.getItem(this.#minnesnyckel) || 'null');
      if (data && Array.isArray(data.valda)) return { storlek: Number(data.storlek) || 0, valda: data.valda.map(Number) };
    } catch {
      // Utan lagring (privat läge, blockerad) börjar lådan tom
    }
    return null;
  }

  #sparaMinne() {
    try {
      if (this.#valda.length) {
        localStorage.setItem(this.#minnesnyckel, JSON.stringify({ storlek: this.#storlek, valda: this.#valda }));
      } else {
        localStorage.removeItem(this.#minnesnyckel);
      }
    } catch {
      // Se #lasMinne
    }
  }
}

/** @param {string} text */
function html(text) {
  return String(text).replace(/[&<>"']/g, (tecken) => `&#${tecken.charCodeAt(0)};`);
}

const attr = html;

if (!customElements.get('kb-bygg-lada-component')) {
  customElements.define('kb-bygg-lada-component', KbByggLadaComponent);
}
